import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Camera, Upload, Image as ImageIcon, Loader2, AlertTriangle, Trash2, RotateCcw, Database, RefreshCw, CheckCircle } from 'lucide-react';
import { BrandLoader } from '../components/BrandLoader';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { PageHeader } from '../components/PageHeader';
import { ResultCard, RichText } from '../components/AIResultSections';

const BACKEND_URL = '';
const API = `${BACKEND_URL}/api`;

export const DermatoPage = () => {
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [ragStatus, setRagStatus] = useState(null);
  const [scrapingInProgress, setScrapingInProgress] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const fileInputRef = useRef(null);
  const mobileFileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  // Fetch RAG status on mount
  useEffect(() => {
    fetchRagStatus();
    const interval = setInterval(fetchRagStatus, 10000); // Refresh every 10s
    return () => clearInterval(interval);
  }, []);

  const fetchRagStatus = async () => {
    try {
      const response = await axios.get(`${API}/dermato/scrape/status`);
      setRagStatus(response.data);
      setScrapingInProgress(response.data.status?.running || false);
    } catch (error) {
    }
  };

  const startScraping = async () => {
    try {
      setScrapingInProgress(true);
      // Initialize with manual database instead of web scraping
      await axios.post(`${API}/dermato/database/init`);
      toast.success('Base de données dermatologique initialisée');
      fetchRagStatus();
      setScrapingInProgress(false);
    } catch (error) {
      toast.error('Erreur lors de l\'initialisation');
      setScrapingInProgress(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.match(/^image\/(jpeg|png|webp)$/)) {
        toast.error('Format accepté: JPEG, PNG ou WEBP uniquement');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (event) => {
        // Redimensionnement/compression avant envoi : évite les payloads multi-Mo
        // (limites proxy en prod + limites image de l'API vision) à l'origine d'erreurs 500.
        const img = new Image();
        img.onload = () => {
          const maxDim = 1400;
          let { width, height } = img;
          if (width > maxDim || height > maxDim) {
            const ratio = Math.min(maxDim / width, maxDim / height);
            width = Math.round(width * ratio);
            height = Math.round(height * ratio);
          }
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          canvas.getContext('2d').drawImage(img, 0, 0, width, height);
          const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
          setImagePreview(dataUrl);
          setImage(dataUrl.split(',')[1]); // Base64 sans le préfixe
          setResult(null);
        };
        img.onerror = () => toast.error('Image illisible, réessayez avec un autre fichier.');
        img.src = event.target.result;
      };
      reader.readAsDataURL(file);
    }
  };

  const startCamera = async () => {
    setCameraError(null);
    
    // Check if getUserMedia is supported
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setCameraError('Votre navigateur ne supporte pas l\'accès à la caméra.');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' }
      });

      streamRef.current = stream;
      
      // Show camera view IMMEDIATELY
      setShowCamera(true);
      
      // Then attach stream to video element
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      }, 100);
      
    } catch (error) {
      
      // Try with any camera
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        streamRef.current = stream;
        setShowCamera(true);
        
        setTimeout(() => {
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
          }
        }, 100);
        
      } catch (err) {
        setCameraError('Impossible d\'accéder à la caméra. Vérifiez les permissions.');
        toast.error('Erreur caméra');
      }
    }
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      
      const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
      setImagePreview(dataUrl);
      setImage(dataUrl.split(',')[1]);
      setResult(null);
      stopCamera();
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setShowCamera(false);
  };

  const clearImage = () => {
    setImage(null);
    setImagePreview(null);
    setResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (mobileFileInputRef.current) {
      mobileFileInputRef.current.value = '';
    }
  };

  const analyzeImage = async () => {
    if (!image) {
      toast.error('Veuillez sélectionner ou capturer une image');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/dermato/analyze`, {
        image_base64: image
      });
      setResult(response.data.analysis);
      toast.success('Analyse terminée');
    } catch (error) {
      toast.error('Erreur lors de l\'analyse');
    } finally {
      setLoading(false);
    }
  };

  const parseAnalysis = (text) => {
    const sections = [];
    let currentSection = null;
    let currentContent = [];

    const sectionConfig = {
      'HYPOTHÈSES': { color: 'blue', title: 'HYPOTHÈSES DIAGNOSTIQUES' },
      'HYPOTHESES': { color: 'blue', title: 'HYPOTHÈSES DIAGNOSTIQUES' },
      'DESCRIPTION': { color: 'slate', title: 'DESCRIPTION LÉSIONNELLE' },
      'PROBABILITÉS': { color: 'indigo', title: 'PROBABILITÉS' },
      'PROBABILITES': { color: 'indigo', title: 'PROBABILITÉS' },
      'URGENCE': { color: 'red', title: 'NIVEAU D\'URGENCE' },
      'EXAMENS': { color: 'cyan', title: 'EXAMENS COMPLÉMENTAIRES' },
      'CAT': { color: 'emerald', title: 'CONDUITE À TENIR' },
      'SOURCES': { color: 'purple', title: 'RÉFÉRENCES' }
    };

    const lines = text.split('\n');
    
    for (const line of lines) {
      const trimmedLine = line.trim().toUpperCase();
      let foundSection = null;
      
      for (const [key, config] of Object.entries(sectionConfig)) {
        if (trimmedLine.includes(`[${key}]`)) {
          foundSection = { key, ...config };
          break;
        }
      }
      
      if (foundSection) {
        if (currentSection && currentContent.length > 0) {
          sections.push({ ...currentSection, content: currentContent.join('\n') });
        }
        currentSection = foundSection;
        currentContent = [];
      } else if (line.trim() && !line.trim().startsWith('━')) {
        currentContent.push(line);
      }
    }

    if (currentSection && currentContent.length > 0) {
      sections.push({ ...currentSection, content: currentContent.join('\n') });
    }

    return sections.length > 0 ? sections : [{ color: 'slate', title: 'ANALYSE', content: text }];
  };

  const colorClasses = {
    blue: 'bg-blue-900/30 border-blue-500/50 text-blue-300',
    slate: 'bg-slate-900/40 border-slate-600/40 text-slate-300',
    indigo: 'bg-indigo-900/30 border-indigo-500/50 text-indigo-300',
    red: 'bg-red-900/30 border-red-500/50 text-red-300',
    cyan: 'bg-cyan-900/30 border-cyan-500/50 text-cyan-300',
    emerald: 'bg-emerald-900/30 border-emerald-500/50 text-emerald-300',
    purple: 'bg-purple-900/30 border-purple-500/50 text-purple-300'
  };

  const sections = result ? parseAnalysis(result) : [];

  return (
    <div data-testid="dermato-page" className="space-y-4 sm:space-y-6">
      {/* Header */}
      <PageHeader icon={ImageIcon} title="Analyse Dermatologique IA" subtitle="Analyse documentaire probabiliste par image" gradient="from-pink-600 to-rose-600" testId="dermato-header" />

      {/* Warning Banner */}
      <div className="bg-amber-900/30 border border-amber-500/50 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-amber-200 font-medium">Avertissement médico-légal</p>
            <p className="text-xs text-amber-300/80 mt-1">
              Cette analyse IA est une aide au diagnostic et ne remplace pas l'examen clinique ni l'avis d'un dermatologue.
              Les probabilités sont indicatives. En cas de doute, orienter vers un spécialiste.
            </p>
          </div>
        </div>
      </div>

      {/* RAG Database Status */}
      <div className="bg-slate-900/60 border border-slate-700 rounded-xl p-3 sm:p-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Database className="w-5 h-5 text-cyan-400 shrink-0" />
            <div>
              <p className="text-sm text-white font-medium">Base de données DermNet NZ</p>
              <p className="text-xs text-slate-400">
                {ragStatus?.database?.total_conditions || 0} conditions • {ragStatus?.database?.with_embeddings || 0} avec embeddings
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {scrapingInProgress ? (
              <div className="flex items-center gap-2 text-xs text-cyan-400">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>{ragStatus?.status?.current || 'En cours...'}</span>
                <span>({ragStatus?.status?.progress || 0}/{ragStatus?.status?.total || 0})</span>
              </div>
            ) : ragStatus?.database?.total_conditions > 0 ? (
              <div className="flex items-center gap-2 text-xs text-emerald-400">
                <CheckCircle className="w-4 h-4" />
                <span>RAG actif</span>
              </div>
            ) : null}
            <Button
              onClick={startScraping}
              disabled={scrapingInProgress}
              size="sm"
              variant="outline"
              className="rounded-full px-4 py-1 text-xs border-cyan-600 text-cyan-400 hover:bg-cyan-900/30"
            >
              {scrapingInProgress ? (
                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              ) : (
                <RefreshCw className="w-3 h-3 mr-1" />
              )}
              {ragStatus?.database?.total_conditions > 0 ? 'Actualiser' : 'Initialiser RAG'}
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Image Upload Section */}
        <div className="glassmorphic rounded-2xl p-3.5 sm:p-6 space-y-4">
          <h2 className="text-base sm:text-xl font-semibold text-white mb-3 sm:mb-4">Image de la lésion</h2>
          
          {/* Camera View with Framing Guide */}
          {showCamera && (
            <div className="relative bg-black rounded-xl overflow-hidden" style={{ minHeight: '300px' }}>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover"
                style={{ minHeight: '300px', maxHeight: '500px' }}
              />
              
              {/* Framing Guide Overlay - Clean version */}
              <div className="absolute inset-0 pointer-events-none">
                {/* Central frame */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="relative w-[70%] max-w-[280px] aspect-square">
                    {/* Clear center - cut out effect */}
                    <div className="absolute inset-0 border-4 border-white/70 rounded-2xl shadow-[0_0_0_2000px_rgba(0,0,0,0.3)]" />
                    
                    {/* Corner markers */}
                    <div className="absolute -top-1 -left-1 w-10 h-10 border-t-4 border-l-4 border-pink-500 rounded-tl-xl" />
                    <div className="absolute -top-1 -right-1 w-10 h-10 border-t-4 border-r-4 border-pink-500 rounded-tr-xl" />
                    <div className="absolute -bottom-1 -left-1 w-10 h-10 border-b-4 border-l-4 border-pink-500 rounded-bl-xl" />
                    <div className="absolute -bottom-1 -right-1 w-10 h-10 border-b-4 border-r-4 border-pink-500 rounded-br-xl" />
                    
                    {/* Center crosshair */}
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                      <div className="relative">
                        <div className="w-8 h-[2px] bg-white/50" />
                        <div className="w-[2px] h-8 bg-white/50 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Camera Controls */}
              <div className="absolute bottom-4 left-0 right-0 flex justify-center items-center gap-6">
                {/* Cancel button */}
                <button
                  onClick={stopCamera}
                  className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm text-white flex items-center justify-center hover:bg-white/30 transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                
                {/* Capture button */}
                <button
                  onClick={capturePhoto}
                  className="w-20 h-20 rounded-full bg-white border-4 border-pink-500 shadow-lg flex items-center justify-center hover:scale-105 transition-transform active:scale-95"
                >
                  <div className="w-14 h-14 rounded-full bg-pink-500" />
                </button>
                
                {/* Spacer for symmetry */}
                <div className="w-12 h-12" />
              </div>
            </div>
          )}

          {/* Image Preview */}
          {!showCamera && imagePreview && (
            <div className="relative">
              <img
                src={imagePreview}
                alt="Lésion à analyser"
                className="w-full max-h-80 object-contain rounded-xl bg-slate-900"
              />
              <button
                onClick={clearImage}
                className="absolute top-2 right-2 p-2 rounded-lg bg-slate-900/80 text-rose-400 hover:bg-slate-800"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </div>
          )}

          {/* Upload Buttons */}
          {!showCamera && !imagePreview && (
            <div className="space-y-4">
              {/* Camera Error Message */}
              {cameraError && (
                <div className="bg-rose-900/30 border border-rose-500/50 rounded-xl p-4">
                  <p className="text-rose-300 text-sm">{cameraError}</p>
                  <p className="text-rose-400/70 text-xs mt-2">
                    💡 Alternative : utilisez "Choisir depuis la galerie" ci-dessous
                  </p>
                </div>
              )}

              {/* Action buttons */}
              <div className="border-2 border-dashed border-slate-700 rounded-xl p-6 text-center">
                <Camera className="w-12 h-12 mx-auto mb-3 text-slate-600" />
                <p className="text-slate-400 mb-4">Prenez une photo de la lésion</p>
                
                <div className="flex flex-col gap-3 justify-center">
                  {/* Main button - Camera with live guide */}
                  <Button
                    data-testid="camera-guide-button"
                    onClick={startCamera}
                    className="rounded-full px-6 py-4 bg-pink-600 hover:bg-pink-500 w-full text-base font-medium"
                  >
                    <Camera className="w-5 h-5 mr-2" />
                    📷 Ouvrir caméra avec guide
                  </Button>
                  
                  {/* Gallery upload */}
                  <Button
                    data-testid="upload-button"
                    onClick={() => fileInputRef.current?.click()}
                    variant="outline"
                    className="rounded-full px-6 py-3 border-slate-600 text-slate-300 hover:bg-slate-800 w-full"
                  >
                    <Upload className="w-5 h-5 mr-2" />
                    Choisir depuis la galerie
                  </Button>
                </div>
                
                <p className="text-slate-500 text-xs mt-4">
                  Si la caméra ne s'ouvre pas, autorisez l'accès dans les paramètres de votre navigateur
                </p>
              </div>
            </div>
          )}

          {/* Hidden file input for gallery */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/*"
            onChange={handleFileSelect}
            className="hidden"
          />
          
          {/* Hidden file input for mobile camera - with capture attribute */}
          <input
            ref={mobileFileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleFileSelect}
            className="hidden"
          />
          
          <canvas ref={canvasRef} className="hidden" />
          {imagePreview && !showCamera && (
            <div className="flex gap-3">
              <Button
                data-testid="analyze-button"
                onClick={analyzeImage}
                disabled={loading}
                className="flex-1 rounded-full px-6 py-3 bg-gradient-to-r from-pink-600 to-rose-600 hover:from-pink-500 hover:to-rose-500 text-white font-medium transition-all shadow-[0_4px_14px_0_rgba(236,72,153,0.39)] hover:shadow-[0_6px_20px_rgba(236,72,153,0.5)] disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Analyse en cours...
                  </>
                ) : (
                  <>
                    <ImageIcon className="w-5 h-5 mr-2" />
                    Analyser la lésion
                  </>
                )}
              </Button>
              <Button
                onClick={clearImage}
                variant="outline"
                className="rounded-full px-4 py-3 border-slate-600 text-slate-300 hover:bg-slate-800"
              >
                <RotateCcw className="w-5 h-5" />
              </Button>
            </div>
          )}
        </div>

        {/* Results Section */}
        <div className="glassmorphic rounded-2xl p-3.5 sm:p-6">
          <h2 className="text-base sm:text-xl font-semibold text-white mb-3 sm:mb-4">Résultat de l'analyse</h2>
          
          {!result && !loading && (
            <div className="h-64 flex items-center justify-center">
              <div className="text-center">
                <ImageIcon className="w-16 h-16 mx-auto mb-4 text-slate-700" />
                <p className="text-slate-500">
                  Uploadez une image pour obtenir une analyse
                </p>
              </div>
            </div>
          )}

          {loading && (
            <div className="h-64 flex items-center justify-center">
              <BrandLoader label="Analyse de l'image en cours…" />
            </div>
          )}

          {result && (
            <div data-testid="analysis-result" className="space-y-2.5 sm:space-y-4 lg:max-h-[500px] lg:overflow-y-auto scrollbar-custom">
              {sections.map((section, index) => (
                <ResultCard key={index} title={section.title} accent={section.color}>
                  <RichText text={section.content} accent={section.color} />
                </ResultCard>
              ))}

              {/* Disclaimer */}
              <div className="mt-4 p-4 rounded-xl bg-slate-900/60 border border-slate-700">
                <p className="text-xs text-slate-400 text-center">
                  <span className="font-semibold">AIDE AU DIAGNOSTIC</span> - Sources: Dermato-info, SFDermato, DermNet, DermIS, DermAtlas, Global Skin Atlas.
                  L'examen clinique et l'avis spécialisé restent indispensables.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
